import dayjs from "dayjs";
import { getBookingStats, getBookingTable } from "../api/rest";
import { getLast12MonthsReportPeriod, getPreviousCalendarMonthPeriod } from "./dashboardReportPeriods";
import { resolveSelectedRangeSubdivision } from "./dashboardPeriods";

/**
 * Previous calendar month: daily bookings for the selected location (location scope only).
 */
export async function fetchLastMonthOverviewStats(locationScopedFilters) {
    const period = getPreviousCalendarMonthPeriod();
    const yourFilters = {
        ...locationScopedFilters,
        startDate: period.start.format("YYYY-MM-DD"),
        endDate: period.end.format("YYYY-MM-DD"),
        experienceId: "all",
        xCubeId: "all",
        options: [],
    };

    const yourBookingsByDay = await fetchStatsBuckets(
        yourFilters,
        period.start,
        period.end,
        "day",
        "play_counts",
    );
    const filterStats = await fetchSelectedRangeFilterStats(yourFilters);

    return {
        period,
        yourBookingsByDay,
        yourMonthTotal: filterStats?.count,
        filterStats,
    };
}

function buildRangeFilters(baseFilters, startDate, endDate) {
    return {
        ...baseFilters,
        startDate: dayjs(startDate).format("YYYY-MM-DD"),
        endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
}

async function fetchStatsBuckets(baseFilters, start, end, subdivision, statistic) {
    const response = await getBookingStats(buildRangeFilters(baseFilters, start, end), {
        subdivision,
        statistic,
    });
    return response.buckets;
}

/** Filter-bar totals (count, players, ratings) from table endpoint; limit 1 avoids large payloads. */
export async function fetchSelectedRangeFilterStats(baseFilters) {
    const { stats } = await getBookingTable({ ...baseFilters, limit: 1 });
    return stats;
}

/** Location scope only — ignores URL date/experience/xcube filters (fixed report window). */
export function buildLocationScopedFilters(baseFilters) {
    return {
        startDate: baseFilters.startDate,
        endDate: baseFilters.endDate,
        locationId: baseFilters.locationId,
        experienceId: "all",
        xCubeId: "all",
        options: [],
        limit: 1,
    };
}

/**
 * Fixed 12-month report (location-reports style): not tied to the selected timeline filters.
 */
export async function fetchFixedReportStats(locationScopedFilters) {
    const period = getLast12MonthsReportPeriod();
    const filters = {
        ...locationScopedFilters,
        startDate: period.start.format("YYYY-MM-DD"),
        endDate: period.end.format("YYYY-MM-DD"),
        experienceId: "all",
        xCubeId: "all",
        options: [],
    };

    const filterStats = await fetchSelectedRangeFilterStats(filters);
    const bookingsByMonth = await fetchStatsBuckets(
        filters,
        period.start,
        period.end,
        "month",
        "play_counts",
    );
    const playersByMonth = await fetchStatsBuckets(
        filters,
        period.start,
        period.end,
        "month",
        "number_of_players",
    );
    const playByExperience = await fetchStatsBuckets(
        filters,
        period.start,
        period.end,
        "experience",
        "play_counts",
    );
    const bookingsByDay = await fetchStatsBuckets(
        filters,
        period.start,
        period.end,
        "day",
        "play_counts",
    );
    const playersByDay = await fetchStatsBuckets(
        filters,
        period.start,
        period.end,
        "day",
        "number_of_players",
    );
    const playByLanguage = await fetchStatsBuckets(
        filters,
        period.start,
        period.end,
        "language",
        "play_counts",
    );

    return {
        period,
        periodLabel: period.label,
        monthKeys: period.monthKeys,
        filterStats,
        bookingsByMonth,
        playersByMonth,
        playByExperience,
        bookingsByDay,
        playersByDay,
        playByLanguage,
    };
}

/**
 * Load dashboard aggregates via GET /bookings/stats (sequential; no parallel limit:10000 table calls).
 * @param {object} baseFilters - URL filter payload
 * @param {ReturnType<import('./dashboardPeriods').getDashboardComparisonPeriods>} periods
 */
export async function fetchDashboardStatsData(baseFilters, periods) {
    const filterStats = await fetchSelectedRangeFilterStats(baseFilters);
    const selectedRangeSubdivision = resolveSelectedRangeSubdivision(
        baseFilters.startDate,
        baseFilters.endDate,
    );

    const selectedRangeBookings = await fetchStatsBuckets(
        baseFilters,
        baseFilters.startDate,
        baseFilters.endDate,
        selectedRangeSubdivision,
        "play_counts",
    );

    // Popular weekdays need day buckets even when the timeline chart uses week/month.
    let selectedRangeBookingsByDay = selectedRangeBookings;
    let selectedRangePlayersByDay;
    if (selectedRangeSubdivision === "day") {
        selectedRangePlayersByDay = await fetchStatsBuckets(
            baseFilters,
            baseFilters.startDate,
            baseFilters.endDate,
            "day",
            "number_of_players",
        );
    } else {
        selectedRangeBookingsByDay = await fetchStatsBuckets(
            baseFilters,
            baseFilters.startDate,
            baseFilters.endDate,
            "day",
            "play_counts",
        );
        selectedRangePlayersByDay = await fetchStatsBuckets(
            baseFilters,
            baseFilters.startDate,
            baseFilters.endDate,
            "day",
            "number_of_players",
        );
    }

    const selectedRangePlayByExperience = await fetchStatsBuckets(
        baseFilters,
        baseFilters.startDate,
        baseFilters.endDate,
        "experience",
        "play_counts",
    );

    const periodRanges = {
        thisWeek: { start: periods.startThisWeek, end: periods.endThisWeek },
        prevWeek: { start: periods.startPrevWeek, end: periods.endPrevWeek },
        prev2Week: { start: periods.startPrev2Week, end: periods.endPrev2Week },
        weekLastYear: { start: periods.startWeekLastYear, end: periods.endWeekLastYear },
        prevMonth: { start: periods.startPrevMonth, end: periods.endPrevMonth },
        monthBefore: { start: periods.startMonthBefore, end: periods.endMonthBefore },
        sameMonthLastYear: {
            start: periods.startSameMonthLastYear,
            end: periods.endSameMonthLastYear,
        },
        ytd: { start: periods.startYtd, end: periods.endYtd },
        ytdPrev: { start: periods.startYtdPrev, end: periods.endYtdPrev },
    };

    const periodBookingsByDay = {};
    for (const [key, { start, end }] of Object.entries(periodRanges)) {
        periodBookingsByDay[key] = await fetchStatsBuckets(
            baseFilters,
            start,
            end,
            "day",
            "play_counts",
        );
    }

    return {
        filterStats,
        selectedRangeSubdivision,
        selectedRange: {
            bookings: selectedRangeBookings,
            bookingsByDay: selectedRangeBookingsByDay,
            playersByDay: selectedRangePlayersByDay,
            playByExperience: selectedRangePlayByExperience,
        },
        periodBookingsByDay,
    };
}
